import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { MarginSnapLines } from '@src/slide/application/service/snap-line-service/render/components/margin-snap-line';
import { LineType } from '@src/slide/application/service/snap-line-service/interface/line.interface';
import { EdgeSnapLines } from '@src/slide/application/service/snap-line-service/render/components/edge-snap-line';
import { RectSnapLines } from '@src/slide/application/service/snap-line-service/render/components/rect-snap-line';
import { DotLine } from '@src/slide/application/service/snap-line-service/render/components/extend-line/dot-line';
import { Orient } from '@src/slide/application/service/snap-line-service/interface/orient.interface';
import { shouldDisplayDebugContent } from '@src/slide/application/service/snap-line-service/util/debug';
import type { ISnapLineService } from '@src/slide/application/service/snap-line-service/interface/snap-line-service.interface';
import type {
    IActiveMarginSnapGridLine,
    IActiveSnapGridLine,
    ISnapGridLine,
} from '@src/slide/application/service/snap-line-service/interface/line.interface';
import type { ISnapLineStyleSheet, IScale } from '@src/slide/application/service/snap-line-service/interface/render.interface';

interface SnapLinesProps {
    // pc端整体在外层会transform，而移动端不会，需要组件自己处理scale
    // 为了保证双端的线在任何scale下，都保持一样的视觉粗细，分为frameScale和graphicScale
    // pc 内部控制粗细等按 graphicScale 放大，frame跟随外层transform
    // mobile 按frameScale处理frame，粗细等不做缩放
    scale: IScale;
    snapLineService: ISnapLineService;
    styleSheet: ISnapLineStyleSheet;
}

@observer
export class SnapLines extends Component<SnapLinesProps> {
    public constructor(props: SnapLinesProps) {
        super(props);
    }

    public render() {
        return <>
            {this.renderActiveSnapLines()}
            {this.renderDevDebugContent()}
        </>;
    }

    private renderActiveSnapLines() {
        const activeSnapLines = this.props.snapLineService?.getActiveSnapLines() || [];

        // 在各自的渲染里面，有需要整体处理、简化渲染的逻辑，所以分组渲染而不是整体循环渲染
        const marginSnapLines: IActiveMarginSnapGridLine[] = [];
        const rectSnapLines: IActiveSnapGridLine[] = [];
        const edgeSnapLines: IActiveSnapGridLine[] = [];
        activeSnapLines.forEach((snapLine) => {
            if (this.isMarginSnapLine(snapLine)) {
                marginSnapLines.push(snapLine);
            } else if (this.isRectSnapLine(snapLine)) {
                rectSnapLines.push(snapLine);
            } else {
                edgeSnapLines.push(snapLine);
            }
        });

        return <>
            <MarginSnapLines
                snapLines={marginSnapLines}
                scale={this.props.scale}
                strokeWidth={this.props.styleSheet.strokeWidth}
                displayPadding={this.props.styleSheet.margin.displayPadding}
                arrowSize={this.props.styleSheet.margin.arrowSize}
                strokeDash={this.props.styleSheet.strokeDash}
            />
            <RectSnapLines
                snapLines={rectSnapLines}
                scale={this.props.scale}
                strokeWidth={this.props.styleSheet.strokeWidth}
                arrowSize={this.props.styleSheet.margin.arrowSize}
                strokeDash={this.props.styleSheet.strokeDash}
            />
            <EdgeSnapLines
                snapLines={edgeSnapLines}
                scale={this.props.scale}
                strokeWidth={this.props.styleSheet.strokeWidth}
                displayPadding={this.props.styleSheet.edge.displayPadding}
                strokeDash={this.props.styleSheet.strokeDash}
            />
        </>;
    }

    private isMarginSnapLine(snapLine: IActiveSnapGridLine): snapLine is IActiveMarginSnapGridLine {
        return snapLine.type === LineType.MARGIN;
    }

    private isRectSnapLine(snapLine: IActiveSnapGridLine) {
        return snapLine.type === LineType.RECT;
    }

    private renderDevDebugContent() {
        if (shouldDisplayDebugContent()) {
            const { snapLineService } = this.props;
            const devGrids = snapLineService?.context.elementGrids || [];
            const detectGrids = snapLineService?.context.detectGrids || [];
            const adsorbPoints = snapLineService?.context.adsorbPoints || [];
            const beforeAdsorbPoints = snapLineService?.context.beforeAdsorbPoints || [];

            return <>
                {
                    devGrids.map((snapLine, index) => <DevSnapLine
                        key={index}
                        snapLine={snapLine}
                        scale={this.props.scale}
                        color={'green'}
                    />)
                }
                {
                    detectGrids.map((snapLine, index) => <DevSnapLine
                        key={index}
                        snapLine={snapLine}
                        scale={this.props.scale}
                        color={'red'}
                    />)
                }
                {
                    adsorbPoints.map((point, index) => <div className={'adsorbPoints'} key={index} style={{
                        position: 'absolute',
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: '#000',
                        left: point.x,
                        top: point.y,
                    }}></div>)
                }
                {
                    beforeAdsorbPoints.map((point, index) => <div className={'beforeAdsorbPoints'} key={index} style={{
                        position: 'absolute',
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: 'red',
                        left: point.x,
                        top: point.y,
                    }}></div>)
                }</>;
        }
    }
}


export function DevSnapLine({ snapLine, scale, color }: { snapLine: ISnapGridLine; scale: IScale; color?: string }) {
    const { orient, position } = snapLine;
    const strokeWidth = 1.5;
    const displayLength = 3000;
    const style = orient === Orient.HORIZONTAL ? {
        width: displayLength,
        height: strokeWidth,
        left: 0,
        top: position,
    } : {
        width: strokeWidth,
        height: displayLength,
        left: position,
        top: 0,
    };

    return <DotLine rect={style} orient={orient} color={color} scale={scale} strokeDash={7}/>;
}
